/**
 * Runtime fallback for stale hashed assets.
 *
 * If a browser tab holds HTML from a previous release (bfcache, an open tab
 * across a deploy, or an intermediary cache), the content-hashed filenames it
 * references may already be gone, so `/assets/index-<oldhash>.js` 404s and the
 * page renders blank. This inline script watches for those failures and
 * recovers by looking up the CURRENT filename for the same entry from a fresh
 * copy of the page HTML, then re-injecting the script/stylesheet.
 *
 * Runs as a classic inline script in <head> so it is installed before any
 * bundle starts loading. Kept dependency-free and tiny.
 */
export const assetRetryScript = `(function(){
try{
  var ASSET=/\\/assets\\/([^\\/?#]+)$/;
  var retried={};        // original url -> true
  var manifest=null;     // filename base -> current url
  var pending=[];
  var reloaded=false;
  // Only reload for a visitor who has interacted with the page. Without
  // interaction the server-rendered content stays in place (same behavior for
  // every client, no user-agent checks), so a renderer never sees a blank or
  // mid-render reload.
  var interacted=false;
  function markInteracted(){interacted=true;}
  ['pointerdown','keydown','touchstart'].forEach(function(t){
    window.addEventListener(t,markInteracted,{capture:true,passive:true});
  });

  function base(name){
    var m=name.match(/^(.*?)-[A-Za-z0-9_-]{6,}(\\.[a-z0-9]+)$/);
    return m?m[1]+m[2]:name;
  }

  function hardReload(){
    if(reloaded||!interacted)return;
    reloaded=true;
    try{
      if(sessionStorage.getItem('__assetReload')==='1')return;
      sessionStorage.setItem('__assetReload','1');
    }catch(e){}
    location.reload();
  }

  function loadManifest(cb){
    if(manifest){cb();return;}
    pending.push(cb);
    if(pending.length>1)return;
    // Same clean URL (no cache-busting query param, which crawlers would
    // otherwise record as separate URLs); freshness comes from cache:'reload'
    // plus no-cache request headers.
    fetch(location.pathname+location.search,
      {cache:'reload',credentials:'same-origin',
       headers:{'Cache-Control':'no-cache','Pragma':'no-cache'}})
      .then(function(r){return r.ok?r.text():Promise.reject(r.status);})
      .then(function(html){
        var map={};
        var re=/(?:src|href)=["'](\\/assets\\/[^"']+)["']/g,m;
        while((m=re.exec(html))){
          var f=m[1].match(ASSET);
          if(f)map[base(f[1])]=m[1];
        }
        manifest=map;
      })
      .catch(function(){manifest={};})
      .then(function(){
        var q=pending;pending=[];
        for(var i=0;i<q.length;i++){try{q[i]();}catch(e){}}
      });
  }

  function retry(el,url){
    if(retried[url])return;
    retried[url]=true;
    loadManifest(function(){
      var f=url.match(ASSET);
      var fresh=f&&manifest?manifest[base(f[1])]:null;
      if(!fresh||fresh===url){hardReload();return;}
      var clone;
      if(el.tagName==='SCRIPT'){
        clone=document.createElement('script');
        clone.src=fresh;
        if(el.type)clone.type=el.type;
        if(el.noModule)clone.noModule=true;
        clone.crossOrigin=el.crossOrigin;
        clone.async=false;
        clone.defer=el.defer;
      }else{
        clone=document.createElement('link');
        clone.rel=el.rel||'stylesheet';
        clone.href=fresh;
      }
      clone.setAttribute('data-asset-retry','1');
      clone.addEventListener('error',hardReload);
      (document.head||document.documentElement).appendChild(clone);
    });
  }

  window.addEventListener('error',function(ev){
    var el=ev.target;
    if(!el||el===window||!el.tagName)return;
    var tag=el.tagName;
    if(tag!=='SCRIPT'&&tag!=='LINK')return;
    var url=el.src||el.href||'';
    if(!url||url.indexOf('/assets/')===-1)return;
    if(el.getAttribute('data-asset-retry')==='1'){hardReload();return;}
    try{url=new URL(url,location.href).pathname;}catch(e){return;}
    retry(el,url);
  },true);

  // Dynamic import() of a stale route chunk rejects instead of firing an
  // element error event; recover by reloading once.
  window.addEventListener('unhandledrejection',function(ev){
    var msg=(ev.reason&&(ev.reason.message||ev.reason))+'';
    if(/(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/i.test(msg)){
      hardReload();
    }
  });

  // A successful load means the current release is intact — clear the guard so
  // a future deploy can reload again.
  window.addEventListener('load',function(){
    try{sessionStorage.removeItem('__assetReload');}catch(e){}
  });
}catch(e){}
})();`;
